import { Spin } from "antd";
import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";

export function StatsModal({ open, onClose, stats, giveaway, loading }) {
  const { language } = useLanguage();
  const t = giveAwayContent[language].statsModal;

  if (!open) return null;

  // Normalisiere die Stats-Daten basierend auf der tatsächlichen Struktur
  const normalizedStats = {
    participants: stats?.participantCount || 0,
    validEntries: stats?.uniqueParticipants || 0,
    views: stats?.views || 0,
    uniqueEmails: stats?.uniqueParticipants || 0,
    winners: stats?.winnerCount || 0,
    status: stats?.status || giveaway?.status,
    isDrawable: stats?.isDrawable || false,
    isEditable: stats?.isEditable || false,
  };

  // Hilfsfunktion für Datumsformatierung
  const formatDate = (date) => {
    if (!date) return t.details.notSet;
    const locale = language === "de" ? "de-DE" : "en-US";
    return new Date(date).toLocaleDateString(locale);
  };

  // Hilfsfunktion für Status-Labels
  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return t.status.active;
      case "ended":
        return t.status.ended;
      case "drawn":
        return t.status.drawn;
      case "upcoming":
        return t.status.upcoming;
      default:
        return t.status.upcoming;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t.title}</h2>
            <p className="text-gray-600 mt-1">
              {giveaway?.title || t.unknownGiveaway}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
            title={t.closeTitle}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <Spin size="large" />
              <p className="mt-4 text-gray-600">{t.loadingStats}</p>
            </div>
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-blue-600 text-sm font-medium">
                      {t.overview.participants}
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {normalizedStats.participants}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-green-600 text-sm font-medium">
                      {t.overview.uniqueParticipants}
                    </p>
                    <p className="text-2xl font-bold text-green-900">
                      {normalizedStats.validEntries}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-purple-600 text-sm font-medium">
                      {t.overview.winners}
                    </p>
                    <p className="text-2xl font-bold text-purple-900">
                      {normalizedStats.winners}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-orange-600 text-sm font-medium">
                      {t.overview.status}
                    </p>
                    <p className="text-sm font-bold text-orange-900">
                      {getStatusLabel(normalizedStats.status)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contest Details */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {t.sections.giveawayDetails}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.details.status}</span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        giveaway?.status === "active"
                          ? "bg-green-100 text-green-800"
                          : giveaway?.status === "ended"
                          ? "bg-red-100 text-red-800"
                          : giveaway?.status === "drawn"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {getStatusLabel(giveaway?.status)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.details.startDate}</span>
                    <span className="font-medium">
                      {formatDate(giveaway?.startDate)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.details.endDate}</span>
                    <span className="font-medium">
                      {formatDate(giveaway?.endDate)}
                    </span>
                  </div>
                  {giveaway?.drawnAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t.details.drawnAt}</span>
                      <span className="font-medium">
                        {formatDate(giveaway.drawnAt)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {t.details.multipleEntries}
                    </span>
                    <span className="font-medium">
                      {giveaway?.allowMultipleEntries
                        ? t.details.allowed
                        : t.details.notAllowed}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.details.autoDraw}</span>
                    <span className="font-medium">
                      {giveaway?.autoDrawEnabled
                        ? t.details.enabled
                        : t.details.disabled}
                    </span>
                  </div>
                </div>
              </div>

              {/* Preise Info */}
              <div className="bg-yellow-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {t.sections.prizes}
                </h3>
                <div className="space-y-2">
                  {giveaway?.prizes?.map((prize, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-yellow-200 last:border-b-0"
                    >
                      <span className="text-gray-700">
                        {prize.title} ({t.prizes.place(prize.place)}):
                      </span>
                      <div className="text-right">
                        <div className="font-medium text-yellow-800">
                          {prize.description}
                        </div>
                        {prize.value && (
                          <div className="text-sm text-yellow-600">
                            {prize.value}
                          </div>
                        )}
                      </div>
                    </div>
                  )) || <p className="text-gray-500">{t.prizes.noPrizes}</p>}
                </div>
              </div>

              {/* Feedback Form Info */}
              {giveaway?.feedbackForm && (
                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    {t.sections.feedbackForm}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {t.feedbackForm.name}
                      </span>
                      <span className="font-medium">
                        {giveaway.feedbackForm.name}
                      </span>
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-600">
                        {t.feedbackForm.description}
                      </span>
                      <p className="text-sm text-gray-700 mt-1">
                        {giveaway.feedbackForm.description}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Teilnahme-Einstellungen */}
              <div className="bg-indigo-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {t.sections.participationSettings}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {t.details.nameRequired}
                    </span>
                    <span className="font-medium">
                      {giveaway?.requiresName ? t.details.yes : t.details.no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {t.details.emailRequired}
                    </span>
                    <span className="font-medium">
                      {giveaway?.requiresEmail ? t.details.yes : t.details.no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.details.canEdit}</span>
                    <span className="font-medium">
                      {normalizedStats.isEditable
                        ? t.details.yes
                        : t.details.no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.details.canDraw}</span>
                    <span className="font-medium">
                      {normalizedStats.isDrawable
                        ? t.details.yes
                        : t.details.no}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t.emptyState.title}
            </h3>
            <p className="text-gray-500">{t.emptyState.description}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            {t.closeButton}
          </button>
        </div>
      </div>
    </div>
  );
}
